import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import {
  Button,
  Card,
  CardContent,
  DataTable,
  Hyperlink,
  FilterBlock,
  type Column,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  useTableExport,
} from "@racoon/ui";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";

import { useListUsersQuery, type RoleKey, type User } from "~/shared/api/user/user.grpc";
import { useListTeamsQuery } from "~/shared/api/team/team.grpc";
import { useUsersUrlState } from "../model/useUsersUrlState";
import { UserFormDialog } from "~/features/user-form/ui/user-form.dialog";
import { InviteDialog } from "~/features/invite-company/ui/invite-company.dialog";
import { getStaffRoleOptions, roleLabel } from "~/shared/lib/role-options";
import { useListNavigation } from "~/shared/hooks/use-list-navigation";
import { reformatPhoneNumber } from "@racoon/lib";
import { ChevronDown } from "lucide-react";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { ConnectProto } from "@racoon/api";

// Export simplified columns for CSV export
const getUsersExportColumns = (t: any): Column<User>[] => [
  {
    id: "idx",
    header: t(Namespace.Employees, "table.index"),
    accessor: (_r, i) => i + 1,
  },
  {
    id: "fio",
    header: t(Namespace.Employees, "table.fullName"),
    accessor: (row) => [row.firstName, row.lastName].filter(Boolean).join(" "),
  },
  {
    id: "position",
    header: t(Namespace.Employees, "table.position"),
    accessor: (row) => roleLabel(t, row.role),
  },
  {
    id: "dl",
    header: t(Namespace.Employees, "table.driverLicense"),
    accessor: (row) => row.driverLicenseNumber ?? "-",
  },
  {
    id: "phone",
    header: t(Namespace.Employees, "table.phone"),
    accessor: (row) => row.phoneNumber ?? "",
  },
  {
    id: "email",
    header: t(Namespace.Employees, "table.email"),
    accessor: (row) => row.email ?? "",
  },
];

/* ─────────────────────────────────── Page ────────────────────────────────── */

export default function UsersPage() {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "users",
    selectedCompanyId || undefined,
  );

  const isMd = useMediaQuery("(min-width: 768px)");
  // UI state. The registry has no row selection: a row's own links open what
  // they name, so there is no mode to enter and nothing to clear.
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<User | null>(null);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const inviteRoleOptions = React.useMemo(() => getStaffRoleOptions(t), [t]);

  const openUser = React.useCallback((user: User) => {
    setEditingUser(user);
    setDialogOpen(true);
  }, []);

  // Every role's teams, for the team filter. `currentData`: never another org's teams.
  const { currentData: teamsData } = useListTeamsQuery(
    { ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [] },
    { skip: !selectedCompanyId },
  );
  const teams = teamsData?.teams;

  // URL state management (first call to get states and handlers)
  const urlStateConfig = useUsersUrlState({
    ownerOrgId: selectedCompanyId,
    data: undefined,
    teams,
  });

  const {
    states,
    selectedState,
    onStateChange,
    onSearchChange,
    searchValue,
    apiQuery,
    filterParams,
    onFilterParamsChange,
  } = urlStateConfig;

  // Fetch users using gRPC
  const { data, isLoading } = useListUsersQuery(apiQuery!, {
    skip: !apiQuery,
  });

  // Second call with actual data for client-side filtering
  const { items: filteredItems, filteredTotal: actualTotal } = useUsersUrlState({
    ownerOrgId: selectedCompanyId,
    data: data?.users,
    teams,
  });

  // Table columns
  type Row = User;
  const columns = React.useMemo<Column<Row>[]>(() => {
    return [
      {
        id: "idx",
        header: t(Namespace.Employees, "table.index"),
        accessor: (_r, i) => i + 1,
        width: 64,
      },
      {
        id: "fio",
        header: t(Namespace.Employees, "table.fullName"),
        width: 320,
        cell: (info) => {
          const row = info.row.original;
          const name = [row.firstName, row.lastName].filter(Boolean).join(" ");
          if (!name) return "-";
          return <Hyperlink onOpen={() => openUser(row)}>{name}</Hyperlink>;
        },
      },
      {
        id: "position",
        header: t(Namespace.Employees, "table.position"),
        width: 180,
        cell: (info) => roleLabel(t, info.row.original.role),
      },
      {
        id: "dl",
        header: t(Namespace.Employees, "table.driverLicense"),
        key: "driverLicenseNumber",
        width: 180,
        cell: (info) => info.getValue() ?? "-",
      },
      {
        id: "phone",
        header: t(Namespace.Employees, "table.phone"),
        key: "phoneNumber",
        width: 200,
        cell: (info) => {
          const phone = info.getValue() as string | undefined;
          if (!phone) return "-";
          // tel: hands the call to whatever the dispatcher's machine registered
          // for it — softphone, paired handset, or nothing at all.
          return <Hyperlink href={`tel:+${reformatPhoneNumber(phone)}`}>{phone}</Hyperlink>;
        },
      },
      {
        id: "email",
        header: t(Namespace.Employees, "table.email"),
        key: "email",
        width: 220,
      },
      {
        id: "status",
        header: t(Namespace.Employees, "table.status"),
        width: 140,
        accessor: (r) => Number(r.status) !== ConnectProto.UserStatus.INACTIVE,
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-1 text-xs text-emerald-700">
              {t(Namespace.Employees, "status.active")}
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-1 text-xs text-slate-700">
              {t(Namespace.Employees, "status.archived")}
            </span>
          ),
      },
    ];
  }, [t, openUser]);

  const navigation = useListNavigation(filteredItems, editingUser, setEditingUser);

  const { handleExportExcel } = useTableExport({
    data: filteredItems,
    columns: getUsersExportColumns(t),
    reportName: "Сотрудники",
  });

  // Default role for dialog based on current tab
  const defaultRole: RoleKey | undefined = React.useMemo(() => {
    const tabValue = selectedState?.value;
    if (tabValue === "dispatchers") return "USER_ROLE_PROVIDER_DISPATCHER";
    if (tabValue === "drivers") return "USER_ROLE_DRIVER";
    if (tabValue === "mechanics") return "USER_ROLE_PROVIDER_MECHANIC";
    if (tabValue === "loaders") return "USER_ROLE_PROVIDER_LOADER";
    if (tabValue === "support") return "USER_ROLE_SUPPORT";
    if (tabValue === "management") return "USER_ROLE_PROVIDER_OWNER";
    return undefined;
  }, [selectedState]);

  return (
    <div className="min-h-screen w-full flex items-start justify-center">
      <Card className="w-full !bg-text-invert border-none">
        <CardContent className="!p-0 space-y-4">
          <FilterBlock
            states={states}
            selectedState={selectedState}
            filterParams={filterParams}
            onFilterParamsChange={onFilterParamsChange}
            onStateChange={onStateChange}
            onSearchChange={onSearchChange}
            searchValue={searchValue}
            totalCount={actualTotal}
            itemType="employees"
            additionalButtons={
              <>
                {/* Add: create an account outright, or send a registration link */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      id="users-add-button"
                      variant="opacityBlueWithOutline"
                      size="sm"
                      aria-label={t(Namespace.Employees, "add")}
                      className="inline-flex items-center gap-2"
                    >
                      <span className="hidden sm:block">{t(Namespace.Employees, "add")}</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[240px] p-0">
                    <DropdownMenuItem
                      id="users-menu-create"
                      className="px-4 py-3 text-sm rounded-none cursor-pointer"
                      onSelect={() => {
                        setEditingUser(null);
                        setDialogOpen(true);
                      }}
                    >
                      {t(Namespace.Employees, "menu.createAccount")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="mx-0 my-0" />
                    <DropdownMenuItem
                      id="users-menu-invite"
                      className="px-4 py-3 text-sm rounded-none cursor-pointer"
                      disabled={!selectedCompanyId}
                      onSelect={() => setInviteOpen(true)}
                    >
                      {t(Namespace.Employees, "menu.generateLink")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Export button */}
                <span className="hidden md:block">
                  <Button
                    id="users-export-button"
                    variant="excel"
                    size="sm"
                    className="inline-flex items-center"
                    onClick={handleExportExcel}
                  >
                    {t(Namespace.Employees, "download")}
                  </Button>
                </span>
              </>
            }
            additionalButtonsPosition={isMd ? "bottom" : "withSearch"}
          />

          {/* Table */}
          <DataTable<Row>
            columns={columns}
            data={filteredItems}
            loading={isLoading}
            initialSort={{ id: "fio", dir: "asc" }}
            rowKey={(r) => String(r.id)}
            selectable={false}
            caption={null}
            zebra
            columnConfig={columnConfig}
            onColumnConfigChange={handleColumnConfigChange}
            namespace={Namespace.Employees}
          />
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <UserFormDialog
        open={dialogOpen}
        onOpenChange={(next) => {
          setDialogOpen(next);
          if (!next) setEditingUser(null);
        }}
        user={editingUser}
        org={{ mode: "fixed", id: selectedCompanyId }}
        staffRolesOnly
        showGroups
        defaultRole={defaultRole}
        navigation={navigation}
      />

      {/* Invite an employee by registration link */}
      {selectedCompanyId && (
        <InviteDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          fixedOrganizationId={selectedCompanyId}
          roleOptions={inviteRoleOptions}
          title={t(Namespace.Employees, "invite.linkTitle")}
        />
      )}
    </div>
  );
}
